// src/components/quiz/ResultsScreen.js
// Riepilogo fine partita, generalizzato per le 3 modalità: la forma del
// breakdown differisce (domande a scelta multipla vs round "indovina il
// giocatore"), quindi la riga di dettaglio si adatta in base a `mode`.
import React from 'react';
import { Trophy, Star, Zap, RotateCcw, Home, ChevronRight, CheckCircle2, XCircle, MinusCircle } from 'lucide-react';
import { fmtTime } from './quizShared';

const MODE_UNIT_LABEL = {
  survival: 'risposte corrette',
  timeattack: 'risposte corrette',
  guess: 'giocatori indovinati',
};

function GuessRow({ item, i }) {
  return (
    <div className={`quiz-bd-row ${item.correct ? 'quiz-bd-row--ok' : 'quiz-bd-row--err'}`}>
      <div className="quiz-bd-row__left">
        <div className="quiz-bd-row__icon">
          {item.correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        </div>
        <div className="quiz-bd-row__text">
          <strong>{i + 1}. {item.playerName} <span className="quiz-bd-row__sub">({item.team})</span></strong>
          <span>
            {item.cluesUsed} indizi{item.wrongGuesses > 0 ? ` · ${item.wrongGuesses} tentativi sbagliati` : ''}
          </span>
          <span className="quiz-bd-row__exp">{item.correct ? 'Indovinato' : 'Arreso'}</span>
        </div>
      </div>
      <div className="quiz-bd-row__score">
        {item.correct ? <><strong>+{item.score}</strong><span>pt</span></> : <strong className="quiz-bd-row__zero">—</strong>}
      </div>
    </div>
  );
}

function QuestionRow({ item, i }) {
  if (item.reached === false) {
    return (
      <div className="quiz-bd-row quiz-bd-row--skip">
        <div className="quiz-bd-row__left">
          <div className="quiz-bd-row__icon"><MinusCircle size={16} /></div>
          <div className="quiz-bd-row__text">
            <strong>{i + 1}. {item.question}</strong>
            <span>Domanda non raggiunta</span>
          </div>
        </div>
        <div className="quiz-bd-row__score"><strong className="quiz-bd-row__zero">—</strong></div>
      </div>
    );
  }

  return (
    <div className={`quiz-bd-row ${item.correct ? 'quiz-bd-row--ok' : 'quiz-bd-row--err'}`}>
      <div className="quiz-bd-row__left">
        <div className="quiz-bd-row__icon">
          {item.correct ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        </div>
        <div className="quiz-bd-row__text">
          <strong>{i + 1}. {item.question}</strong>
          {item.selectedIndex !== null && item.selectedIndex !== undefined ? (
            <span>
              Hai risposto: <em>
                {item.options[item.selectedIndex]?.label}
                {item.options[item.selectedIndex]?.statValue != null && ` (${item.options[item.selectedIndex].statValue})`}
              </em>
            </span>
          ) : (
            <span>Tempo scaduto</span>
          )}
          <span className="quiz-bd-row__exp">Risposta corretta: {item.explanation}</span>
        </div>
      </div>
      <div className="quiz-bd-row__score">
        {item.correct ? <><strong>+{item.score}</strong><span>pt</span></> : <strong className="quiz-bd-row__zero">—</strong>}
      </div>
    </div>
  );
}

export default function ResultsScreen({ mode, breakdown, totalScore, totalSecs, extra, onPlayAgain, onHome }) {
  const isGuess = mode === 'guess';
  const correct = breakdown.filter(b => b.correct).length;
  const countedTotal = isGuess ? breakdown.length : breakdown.filter(b => b.reached !== false).length;
  const pct = countedTotal ? Math.round((correct / countedTotal) * 100) : 0;
  const unitLabel = MODE_UNIT_LABEL[mode] || 'risposte corrette';

  return (
    <div className="quiz-results">
      {/* Summary */}
      <div className="quiz-results__summary glass">
        <div className="quiz-results__glow" />
        <div className="quiz-results__icon">
          {pct >= 80 ? <Trophy size={32} /> : pct >= 50 ? <Star size={32} /> : <Zap size={32} />}
        </div>
        <h2 className="quiz-results__score">{totalScore} <span>punti</span></h2>
        <p className="quiz-results__sub">
          {correct} / {countedTotal} {unitLabel}
          {mode === 'timeattack' && countedTotal < breakdown.length && ` (di ${breakdown.length})`}
          {' '}· {fmtTime(totalSecs)} totali
        </p>
        <div className="quiz-results__chips">
          <span className={`quiz-chip ${pct >= 70 ? 'quiz-chip--green' : 'quiz-chip--amber'}`}>
            {pct}% corretto
          </span>
          <span className="quiz-chip">
            Media {countedTotal ? Math.round(totalScore / countedTotal) : 0} pt/{isGuess ? 'round' : 'domanda'}
          </span>
          {extra?.speedBonus > 0 && (
            <span className="quiz-chip quiz-chip--green">Bonus velocità +{extra.speedBonus} pt</span>
          )}
        </div>
        <div className="quiz-results__actions">
          <button className="quiz-btn quiz-btn--primary" onClick={onPlayAgain}>
            <RotateCcw size={15} /> Gioca ancora
          </button>
          <button className="quiz-btn quiz-btn--ghost" onClick={onHome}>
            <Home size={15} /> Home
          </button>
        </div>
      </div>

      {/* Breakdown */}
      <div className="quiz-breakdown glass">
        <div className="quiz-panel__head">
          <ChevronRight size={15} />
          <h2>Riepilogo {isGuess ? 'round' : 'domande'}</h2>
        </div>
        <div className="quiz-breakdown__list">
          {breakdown.map((item, i) => (
            isGuess
              ? <GuessRow key={i} item={item} i={i} />
              : <QuestionRow key={i} item={item} i={i} />
          ))}
        </div>
      </div>
    </div>
  );
}
